import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, X, ExternalLink } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { supabase } from '@/integrations/supabase/client';
import logoImage from '@/assets/logo-black.png';

interface SiteSettings {
  logo_path?: string;
  company_name: string;
  company_subtitle: string;
}

const Navigation = () => {
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    company_name: 'Croostenburght',
    company_subtitle: 'Beheer'
  });
  const [logoVersion, setLogoVersion] = useState<number>(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { href: '/', label: 'Home' },
    { href: '/verhuur', label: 'Verhuur' },
    { href: '/projecten', label: 'Projecten' },
    { href: '/over-ons', label: 'Over Ons' },
    { href: '/contact', label: 'Contact' },
  ];

  const getImageUrl = (path?: string | null) => {
    if (!path) return null;
    // If it's already a full URL or a local path, use it directly
    if (/^https?:\/\//.test(path) || path.startsWith('/')) {
      return path;
    }
    const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
    return data.publicUrl;
  };

  useEffect(() => {
    const fetchSiteSettings = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('logo_path, company_name, company_subtitle')
        .limit(1)
        .single();
      
      if (data) {
        setSiteSettings(data);
      }
    };

    fetchSiteSettings();
  }, []);

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const updated = e.detail;
      setSiteSettings((prev) => ({ ...prev, ...updated }));
      setLogoVersion(Date.now());
    };
    window.addEventListener('site-settings-updated', handler as unknown as EventListener);
    return () => window.removeEventListener('site-settings-updated', handler as unknown as EventListener);
  }, []);

  const logoSrc = getImageUrl(siteSettings.logo_path);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img 
              src={logoImage} 
              alt="Stichting Croostenburght" 
              className="h-36 md:h-44 w-auto object-contain"
              width="220"
              height="220"
            />
          </a>

          {/* Desktop Navigation Menu */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a 
                key={item.href}
                href={item.href} 
                className="text-sm font-medium text-primary/80 transition-elegant hover:text-primary relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
          </nav>

          {/* Desktop Tenant Portal CTA */}
          <a href="https://stichting-croostenburght.vastgoed.app" target="_blank" rel="noopener noreferrer" className="hidden md:block">
            <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white gap-2 px-4">
              <ExternalLink className="h-4 w-4" />
              Huurders Portal
            </Button>
          </a>

          {/* Mobile Hamburger Menu */}
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="text-primary">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Menu openen</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-full bg-background border-none p-0">
              <div className="flex flex-col h-full">
                {/* Header with close button */}
                <div className="flex items-center justify-between p-6 border-b border-border/50">
                  <img 
                    src={logoImage} 
                    alt="Stichting Croostenburght" 
                    className="h-32 w-auto object-contain"
                    width="128"
                    height="128"
                  />
                </div>

                {/* Navigation Items */}
                <nav className="flex-1 flex flex-col justify-center px-6 py-8 gap-6">
                  {navItems.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-2xl font-medium text-primary hover:text-primary/80 transition-colors py-2"
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>

                {/* Huurders Portal Button */}
                <div className="p-6 border-t border-border/50">
                  <a 
                    href="https://stichting-croostenburght.vastgoed.app" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="block"
                  >
                    <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white gap-2 py-6 text-lg">
                      <ExternalLink className="h-5 w-5" />
                      Huurders Portal
                    </Button>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navigation;